import {
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  Modal,
  OTPStep,
  StatusIndicator,
} from '@unified-checkout/design-system';

import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import {
  BILLS_OTP_MAX_WIDTH,
  MODAL_STEP_STATUS,
  OTP_STATUS,
} from '@app/dashboard/constants/Bills';
import {
  BILLS_EVENTS,
  BILLS_EVENTS_PARAMS
} from '@app/dashboard/constants/tracking_events/bills';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';
import { OTP_LENGTH, THREE_SECONDS, TWO_MINUTES } from '@app/dashboard/constants/shared';

import { verifyOTP } from '@app/dashboard/services/inner_layout/bills/verify-otp';

import type { IOTPModal } from '@app/dashboard/types/innerLayout/bills/otp-modal';

import { isStatusVisible } from '../../../utils';
import styles from './otp-modal.module.scss';

export const OTPModal = ({
  showOtp,
  setShowOtp,
  transferInputs,
  resendOtp,
  phoneNumber
}: IOTPModal) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [otpState, setOtpState] = useState({
    otp: '',
    isOtpValid: false,
    otpResetTimerKey: 0,
  });
  const [otpStatus, setOtpStatus] = useState<string>(MODAL_STEP_STATUS.OTP);
  const [errorMessage, setErrorMessage] = useState('');
  const [transferErrorMessage, setTransferErrorMessage] = useState('');

  const { otp, isOtpValid, otpResetTimerKey } = otpState;

  const recipientMid = transferInputs?.receiver_merchant_id;
  const transferAmount = transferInputs?.amount;
  const isSavedMerchant = !!(transferInputs?.save_beneficiary);

  const verifyOTPPayload = {
    receiver_merchant_id: recipientMid,
    amount: transferAmount,
    save_beneficiary: isSavedMerchant,
    otp
  };

  const {
    mutate: handleVerifyOTP,
    isPending: isVerifyingOtp,
  } = useMutation({
    mutationFn: () => verifyOTP(verifyOTPPayload),
    onSuccess: () => {
      amplitude.track(BILLS_EVENTS.BILLS_TRANSFER_OTP_VERIFIED, {
        [SHARED_EVENTS_PARAMS.AMOUNT]: transferAmount,
        [BILLS_EVENTS_PARAMS.RECEIPT_MID]: recipientMid,
      });
      amplitude.track(BILLS_EVENTS.BILLS_TRANSFER_COMPLETED, {
        [SHARED_EVENTS_PARAMS.AMOUNT]: transferAmount,
        [BILLS_EVENTS_PARAMS.RECEIPT_MID]: recipientMid,
        [BILLS_EVENTS_PARAMS.IS_SAVE_BENEFICIARY]: isSavedMerchant,
      });
      setErrorMessage('');
      setOtpStatus(MODAL_STEP_STATUS.SUCCESS);
      setOtpState((prev) => ({
        ...prev,
        otp: '',
        isOtpValid: false,
      }));

      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_MAIN_DATA] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_SAVED_MERCHANTS] });

      setTimeout(() => {
        handleOtpModalClose();
      }, THREE_SECONDS);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || t('errors.somethingWentWrong');
      setErrorMessage(errorMessage);

      amplitude.track(BILLS_EVENTS.BILLS_TRANSFER_FAILED, {
        [SHARED_EVENTS_PARAMS.FAILURE_REASON]: errorMessage,
      });
    },
  })

  const handleOtpModalClose = () => {
    setShowOtp(false);
    setOtpState((prev) => ({
      ...prev,
      otp: '',
      isOtpValid: false,
    }));
  };

  const handleOtpResend = () => {
    resendOtp({
      receiver_merchant_id: recipientMid,
      amount: transferAmount,
      save_beneficiary: isSavedMerchant,
    });
    setOtpState((prev) => ({
      ...prev,
      otp: '',
      isOtpValid: false,
      otpResetTimerKey: prev.otpResetTimerKey + 1,
    }));
  };

  const handleOtpChange: Dispatch<SetStateAction<string>> = (value) => {
    setOtpState((prev) => ({
      ...prev,
      otp: typeof value === 'function' ? value(prev.otp) : value,
    }));
  };

  const handleOtpSubmit = () => {
    handleVerifyOTP()
  };

  useEffect(() => {
    if (showOtp) {
      setOtpState((prev) => ({
        ...prev,
        otp: '',
        isOtpValid: false,
        otpResetTimerKey: prev.otpResetTimerKey + 1,
      }));
      setOtpStatus(MODAL_STEP_STATUS.OTP);
      setErrorMessage('');
      setTransferErrorMessage('');
    }
  }, [showOtp]);

  const modalBtns = !isStatusVisible(otpStatus) ?
    [
      {
        text: t('form.submit'),
        onClickHandler: () => {
          handleOtpSubmit();
        },
        disabled: !isOtpValid,
        loading: isVerifyingOtp,
      },
      {
        color: "secondary" as const,
        variant: "outlined" as const,
        text: t('form.cancel'),
        onClickHandler: handleOtpModalClose,
      },
    ]
    :
    undefined;

  const otpStatusContent = OTP_STATUS(t).find((item) => item.id === otpStatus);

  return (
    <Modal
      state="primary"
      hasCloseIcon
      maxWidth={otpStatus === MODAL_STEP_STATUS.OTP ? BILLS_OTP_MAX_WIDTH : ''}
      open={showOtp}
      onCloseHandler={handleOtpModalClose}
      closeOnClickOutside={false}
      title={isStatusVisible(otpStatus) ? '' : t('form.xDigitsCode').replace('{{num}}', OTP_LENGTH)}
      supportingText={
        isStatusVisible(otpStatus) &&
        t('form.enterTheCode').replace('{{phoneNum}}', phoneNumber)
      }
      errorMsg={errorMessage}
      btnsProps={modalBtns}
    >
      <div className={styles['modal__body']}>
        {isStatusVisible(otpStatus) ?
          otpStatusContent &&
          <StatusIndicator
            status={otpStatusContent.status}
            icon={otpStatusContent.icon}
            hintText={otpStatusContent.hintText}
          />
          :
          <OTPStep
            otp={otp}
            setOtp={handleOtpChange}
            onValid={(valid) => {
              setOtpState((prev) => ({
                ...prev,
                isOtpValid: valid,
              }));
            }}
            onResendOTP={handleOtpResend}
            otpResetTimerKey={otpResetTimerKey}
            resendText={t('form.resendCodeIn')}
            resendBtnText={t('form.resend')}
            resendTimer={TWO_MINUTES}
            length={OTP_LENGTH}
          />
        }
      </div>
    </Modal>
  );
};

